// ============================================================
// teamMembers.js
// Who is on the team, what they may do, and what name the app knows them by.
//
// ROLES NOW LIVE IN FIRESTORE — in the `teamRoles` collection, editable from
// Settings, so a new hire no longer needs a code change and a deploy.
//
// ⚠️ THIS FILE IS THE FALLBACK, AND THAT IS ITS WHOLE JOB.
//
// The danger this guards against: if role resolution cannot match the
// signed-in email, the user drops to the most restricted role and loses
// Settings — which is the only screen where roles can be changed. There is then
// no way back in from inside the app. So a failed, empty or incomplete
// Firestore read must NEVER produce that outcome.
//
// TWO INDEPENDENT GUARDS, in this order:
//   1. BREAK_GLASS_EMAIL below always resolves to admin, before any lookup —
//      database or file. See resolveRoleFor.
//   2. This hard-coded list answers whenever Firestore cannot: the read failed,
//      the collection is empty, or it holds no record for this address.
//
// A hard-coded list can be wrong, but it is wrong visibly, in a diff, and it
// cannot be wrong differently on one machine than on another.
//
// MAINTENANCE: the five accounts below are the floor. People added in Settings
// live in Firestore ONLY, so during a Firestore outage they fall back to the
// restricted role — worth adding a long-term hire here too, in a normal deploy.
//
// The stale `users` collection is still deliberately untouched: its seed data
// uses @atlanticfence.com, the wrong domain for these accounts. `teamRoles` is
// a separate, new collection — nothing reads or writes `users`.
// ============================================================

// The real accounts and their roles, in whatever order. Sorting for display
// happens below.
//
// ROLES NOW MEAN SOMETHING. 'admin' is the office: the whole app. 'shop' is a
// workshop tablet: one screen, and no route off it.
//
// Adding a person here is the ONLY way they get office access — an address that
// is not on this list resolves to 'shop', the most restricted role, rather than
// to admin. See UNKNOWN_EMAIL_ROLE in RoleContext.js.
export const TEAM_MEMBER_ROLES = [
  { email: 'brett@atlanticfencesupply.com', role: 'admin' },
  { email: 'logan@atlanticfencesupply.com', role: 'admin' },
  { email: 'steve@atlanticfencesupply.com', role: 'admin' },
  { email: 'jordan@atlanticfencesupply.com', role: 'admin' },
  { email: 'sales@atlanticfencesupply.com', role: 'admin' },
  { email: 'shop@atlanticfencesupply.com', role: 'shop' },
];

// ── CAPABILITIES: EXTRAS ON TOP OF A ROLE, NOT A NEW ROLE ──
//
// Placing or lifting a CREDIT HOLD on an account customer is the owner's call
// and Jordan's. The other three admins keep every other admin power and see the
// hold and its reason — they just cannot place or lift one. That is a
// capability, not a rank: making a fourth role would have meant deciding where
// it sits against 'admin' everywhere else in the app, for one flag.
//
// ⚠️ THIS IS A UI-LEVEL RESTRICTION ONLY. The repo carries no Firestore rules
// (firebase.json configures hosting alone), so any authenticated session can
// write creditHold — or anything else — straight from the console. This hides a
// button from colleagues who should not press it. It does not stop anyone who
// means to.
const CREDIT_HOLD_EMAILS = [
  'sales@atlanticfencesupply.com',
  'jordan@atlanticfencesupply.com',
];

/** May this signed-in email place or lift a credit hold? */
export const canManageCreditHoldEmail = (email) =>
  CREDIT_HOLD_EMAILS.includes(String(email || '').trim().toLowerCase());

// Kept as its own export because the mention picker and the display-name
// helpers below only ever needed the addresses.
export const TEAM_MEMBER_EMAILS = TEAM_MEMBER_ROLES.map(m => m.email);

/**
 * A person's display name is the UPPERCASED EMAIL PREFIX, and it is DERIVED
 * here rather than written down a second time.
 *
 * That form is not a style choice — it is the app's actual identifier for a
 * person. Dashboard.js and Sidebar.js both compute it as
 * `email.split('@')[0].toUpperCase()` from the live auth session, and every
 * saved document stores it as `salesperson`, as does every board comment's
 * `author`. Hard-coding the strings alongside the emails would let the two
 * drift the moment an address changed, and a mention would silently stop
 * matching the person it names.
 */
export const displayNameFromEmail = (email) =>
  String(email || '').split('@')[0].toUpperCase();

// { email, name, role } for each account, name derived from email.
export const TEAM_MEMBERS = TEAM_MEMBER_ROLES.map(({ email, role }) => ({
  email,
  name: displayNameFromEmail(email),
  role,
}));

/**
 * The role for a signed-in email, or null when the address is not on the team.
 *
 * Case-insensitive and trimmed, because an auth email arrives however the
 * person typed it into the login box. Returning null rather than a default is
 * deliberate: deciding what an unrecognised account may do is the CALLER's
 * problem, and RoleContext makes that decision in one visible place.
 */
export function getRoleForEmail(email) {
  const key = String(email || '').trim().toLowerCase();
  if (!key) return null;
  const match = TEAM_MEMBER_ROLES.find(m => m.email.trim().toLowerCase() === key);
  return match ? match.role : null;
}

/**
 * Display names from THIS FILE ONLY, sorted alphabetically.
 *
 * The @mention picker uses mentionableNamesFrom() below, which merges these
 * with whoever Firestore knows about. This stays as the file-only floor.
 */
export function getMentionableNames() {
  return TEAM_MEMBERS.map(m => m.name).sort((a, b) => a.localeCompare(b));
}

// ════════════════════════ RESOLUTION ════════════════════════
//
// Everything below is PURE: it takes the Firestore records as an argument and
// never reads them itself. RoleContext does the I/O and hands the result in, so
// the rules — which are the lockout-critical part — can be reasoned about and
// tested without a database.

// ── BREAK GLASS ──
// This address always resolves to admin, whatever any lookup says, and it
// always carries the credit-hold capability.
//
// Settings is the only screen where roles can be changed, and it is admin-only.
// A mistake anywhere else — a typo, a record deleted by accident, an empty
// collection, a bad merge — could otherwise lock the owner out of the one place
// that mistake could be fixed, from inside a deployed app, with no way back.
//
// It lives here rather than in RoleContext so that Settings can read it too:
// the UI must refuse to clear this account's capability, and a second copy of
// the address is exactly how that guard would rot.
export const BREAK_GLASS_EMAIL = 'sales@atlanticfencesupply.com';

/** The roles that exist. There is no third one; capabilities are flags. */
export const ROLES = ['admin', 'shop'];

// What an authenticated address NOBODY has granted access to resolves to: the
// MOST restricted role, not the least. A stray or compromised account should
// not walk into pricing and customers.
export const RESTRICTED_ROLE = 'shop';

/** Trimmed, lowercased — an auth email arrives however it was typed. */
export const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const isBreakGlass = (email) => normalizeEmail(email) === BREAK_GLASS_EMAIL;

/** One record's shape, from either source. displayName is always DERIVED. */
const shapeRecord = (email, role, creditHold, source) => ({
  email: normalizeEmail(email),
  // ⚠️ DERIVED, NEVER STORED. This string is the app's identity for a person:
  // every document's `salesperson`, every board comment's `author`, every
  // notification's recipient. Storing a second copy in Firestore would let the
  // two drift and strand every record already written under the old string.
  name: displayNameFromEmail(email),
  role,
  canManageCreditHold: !!creditHold || isBreakGlass(email),
  source,
});

/** The file's people, in record shape. */
export const fileTeamRecords = () =>
  TEAM_MEMBER_ROLES.map(m => shapeRecord(m.email, m.role, canManageCreditHoldEmail(m.email), 'file'));

/**
 * The effective team: the file, overridden per-email by Firestore.
 *
 * MERGE, never replace. An outage or an empty collection still leaves the five
 * known accounts present, which is what keeps the picker — and the role table —
 * from going blank.
 */
export function mergeTeamRecords(dbRecords) {
  const byEmail = new Map();
  fileTeamRecords().forEach(r => byEmail.set(r.email, r));
  (Array.isArray(dbRecords) ? dbRecords : []).forEach(r => {
    const email = normalizeEmail(r?.email);
    if (!email) return;
    byEmail.set(email, shapeRecord(email, r.role, r.canManageCreditHold, byEmail.has(email) ? 'both' : 'db'));
  });
  return [...byEmail.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The role for a signed-in email, and where it came from.
 *
 * ORDER — this is the lockout guard, and it is the whole point of the module:
 *   1. break glass       — before any lookup, file or database;
 *   2. Firestore record  — what Settings writes;
 *   3. this file         — whenever Firestore cannot answer: read FAILED, read
 *                          empty, or no record for this address;
 *   4. restricted role   — genuinely unknown to both.
 *
 * `readOk` false means the Firestore read failed. It never changes 2→4; it only
 * changes what the warning at step 4 says, so a failed read and a genuine miss
 * are distinguishable in the console.
 */
export function resolveRoleFor(email, { records, readOk } = {}) {
  const address = normalizeEmail(email);
  if (!address) return { role: RESTRICTED_ROLE, source: 'none' };
  if (address === BREAK_GLASS_EMAIL) return { role: 'admin', source: 'break-glass' };

  const db = (Array.isArray(records) ? records : []).find(r => normalizeEmail(r?.email) === address);
  if (db && ROLES.includes(db.role)) return { role: db.role, source: 'db' };

  const fileRole = getRoleForEmail(address);
  if (fileRole) return { role: fileRole, source: 'file' };

  return { role: RESTRICTED_ROLE, source: readOk === false ? 'unknown-read-failed' : 'unknown' };
}

/**
 * May this email place or lift a credit hold?
 *
 * Break glass first and unconditionally, then the Firestore record, then the
 * file's list. Same shape as the role, for the same reason.
 */
export function resolveCreditHoldFor(email, { records } = {}) {
  const address = normalizeEmail(email);
  if (!address) return false;
  if (address === BREAK_GLASS_EMAIL) return true;
  const db = (Array.isArray(records) ? records : []).find(r => normalizeEmail(r?.email) === address);
  if (db) return !!db.canManageCreditHold;
  return canManageCreditHoldEmail(address);
}

/** Every mentionable display name — file merged with Firestore, deduped. */
export const mentionableNamesFrom = (dbRecords) =>
  [...new Set(mergeTeamRecords(dbRecords).map(r => r.name))].sort((a, b) => a.localeCompare(b));

// ── GUARDS: the two ways one click becomes a lockout ──
//
// Both are checked in the Settings HANDLERS as well as in the JSX. The JSX
// decides what a person sees; these decide what the app writes.

/** The admins in an effective (merged) list. */
export const adminEmailsIn = (merged) =>
  (merged || []).filter(r => r.role === 'admin').map(r => r.email);

/**
 * Why this change must be refused, or null when it is allowed.
 *
 * `nextRole` null means removal. The signed-in user is passed in so demoting or
 * removing YOURSELF is caught — one click from losing the screen you are on.
 */
export function roleChangeBlockedReason(merged, targetEmail, nextRole, signedInEmail) {
  const target = normalizeEmail(targetEmail);
  const me = normalizeEmail(signedInEmail);
  const current = (merged || []).find(r => r.email === target);
  if (!current) return null;                       // not in the list: nothing to lose
  const losingAdmin = current.role === 'admin' && nextRole !== 'admin';
  if (!losingAdmin) return null;                   // only admin loss can lock anyone out
  if (target === me) {
    return nextRole === null
      ? 'You cannot remove your own admin access.'
      : 'You cannot change your own role away from admin.';
  }
  if (adminEmailsIn(merged).length <= 1) {
    return nextRole === null
      ? 'This is the last admin — removing them would leave nobody who can manage users.'
      : 'This is the last admin — changing their role would leave nobody who can manage users.';
  }
  return null;
}

// ── The owner ──
//
// One account writes announcements. Kept HERE rather than typed into a page so
// the address exists in exactly one place alongside the rest of the team, and
// so the display name is derived by the same rule as everyone else's.
//
// ⚠️ THIS IS NOT SECURITY. It hides a menu item and a route. The role system in
// RoleContext.js is stubbed out — every signed-in user is isAdmin: true — and
// the repo carries no Firestore rules at all (firebase.json configures hosting
// only), so any authenticated session can write to `announcements` directly
// from the console or a devtools call. This gate stops a colleague clicking
// into a screen that is not theirs. It does not stop anyone who means to.
export const OWNER_EMAIL = 'sales@atlanticfencesupply.com';
export const OWNER_NAME = displayNameFromEmail(OWNER_EMAIL);

/** True when this signed-in email owns announcement authoring. */
export const isOwnerEmail = (email) =>
  String(email || '').trim().toLowerCase() === OWNER_EMAIL;
